// lit-router-nested.ts - LitElement Router with support for nested route elements

import { LitElement, html, css, TemplateResult, PropertyValues, render } from 'lit';
import { property, state, query, customElement } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

// Type definitions
export type EwcRouteParams = Record<string, string>;

export type EwcRouteComponent = 
  | TemplateResult 
  | HTMLElement 
  | string 
  | ((params: EwcRouteParams) => TemplateResult | HTMLElement | string | Promise<TemplateResult | HTMLElement | string>);

export interface LazyRouteComponent {
  lazy: boolean;
  component: () => Promise<EwcRouteComponent>;
}

export type EwcRoute = EwcRouteComponent | LazyRouteComponent;

/**
 * Router service for programmatic navigation
 */
export class EwcRouterService {
  private outlet: EwcRouterOutlet | null = null;
  
  constructor() {
    // Set as global for easy access
    if ((window as any).routerService) {
      return (window as any).routerService;
    }
    
    (window as any).routerService = this;
  }
  
  /**
   * Register router outlet
   * @param outlet - Router outlet instance
   * @returns RouterService for chaining
   */
  registerOutlet(outlet: EwcRouterOutlet): EwcRouterService {
    this.outlet = outlet;
    return this;
  }
  
  /**
   * Navigate programmatically
   * @param path - Path to navigate to
   * @param replaceState - Whether to replace or push state
   */
  navigate(path: string, replaceState = false): void {
    if (this.outlet) {
      this.outlet.navigate(path, replaceState);
    } else {
      // Try to find router outlet
      const outlet = document.querySelector('ewc-router-outlet') as EwcRouterOutlet;
      if (outlet && typeof outlet.navigate === 'function') {
        outlet.navigate(path, replaceState);
      } else {
        console.error('Router outlet not registered. Call registerOutlet first.');
      }
    }
  }
  
  /**
   * Get current route parameters
   * @returns Route parameters
   */
  getParams(): EwcRouteParams {
    return this.outlet ? this.outlet.getParams() : {};
  }
}

// Create and export singleton router service
export const ewcRouterService = new EwcRouterService();

/**
 * Router Route component that defines a route in declarative way
 * @element ewc-route
 */
@customElement('ewc-route')
export class EwcRouterRoute extends LitElement {
  @property({ type: String }) path = '';
  @property({ type: Boolean }) default = false;
  @property({ type: Boolean }) lazy = false;
  
  // Flag to determine if this route is processed
  private _processed = false;
  
  constructor() {
    super();
    this.style.display = 'none'; // Hide the element
  }
  
  /**
   * Get the component content for this route
   */
  getComponent(): EwcRoute {
    console.log('[Route] getComponent called for path:', this.path);
    // If lazy attribute is set
    if (this.lazy) {
      return {
        lazy: true,
        component: async () => {
          // Return slot content wrapped in a function
          return (params: EwcRouteParams) => {
            // Create a container element to wrap the content
            const container = document.createElement('template');
            
            // Clone all light DOM children and append them to the container's content
            Array.from(this.childNodes).forEach(node => {
              container.content.appendChild(node.cloneNode(true));
            });
            
            return container;
          };
        }
      };
    }
    
    // Return content as a function that returns the slotted content
    return (params: EwcRouteParams) => {
      console.log('[Route] Creating template for params:', params);
      // Create a container element to wrap the content
      const container = document.createElement('template');
      
      // Clone all light DOM children and append them to the container's content
      Array.from(this.childNodes).forEach(node => {
        container.content.appendChild(node.cloneNode(true));
      });
      
      return container;
    };
  }
  
  /**
   * Mark this route as processed
   */
  markAsProcessed(): void {
    this._processed = true;
  }
  
  /**
   * Check if this route has been processed
   */
  isProcessed(): boolean {
    return this._processed;
  }
  
  /**
   * Return empty render since we don't want to display anything
   */
  render(): TemplateResult {
    return html`<slot></slot>`;
  }
}

/**
 * EwcRouterOutlet component that handles route rendering
 * @element ewc-router-outlet
 */
@customElement('ewc-router-outlet')
export class EwcRouterOutlet extends LitElement {
  @property({ type: Boolean }) loading = false;
  @property({ type: Boolean }) autoDiscoverRoutes = true;
  @state() private currentPath = '';
  @state() private currentParams: EwcRouteParams = {};
  
  // Internal properties
  private routes: Map<string, EwcRoute> = new Map();
  @query('slot') private routeSlot!: HTMLSlotElement;
  
  static styles = css`
    :host {
      display: block;
    }
    
    .router-content {
      min-height: 200px;
    }
    
    .fade-in {
      animation: fadeIn 0.3s ease-in-out;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    .loading {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 20px;
    }
    
    slot {
      display: none;
    }
  `;
  
  constructor() {
    super();
    
    // Bind methods
    this.navigate = this.navigate.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
  }
  
  connectedCallback(): void {
    super.connectedCallback();
    
    ewcRouterService.registerOutlet(this);
    // Add event listeners for navigation
    window.addEventListener('popstate', this.handlePopState);
    
    // Intercept link clicks for SPA navigation
    document.addEventListener('click', (e: MouseEvent) => {
      // Find closest anchor tag
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      if (!anchor) return;
      
      // Only handle links that should be routed internally
      if (anchor.hasAttribute('ewc-router-link')) {
        e.preventDefault();
        const path = anchor.getAttribute('href') || '/';
        this.navigate(path);
      }
    });
  }
  
  /**
   * When first updated, discover child route elements
   */
  firstUpdated(): void {
    if (this.autoDiscoverRoutes) {
      // Process any route elements that might be in the light DOM
      this.processRouteElements();
      
      // Also listen for slot changes to handle route elements added dynamically
      if (this.routeSlot) {
        this.routeSlot.addEventListener('slotchange', () => {
          this.processRouteElements();
          // Re-resolve route when slot changes
          if (this.routes.size > 0) {
            this.resolveRoute(window.location.pathname);
          }
        });
      }
    }
    
    // Initial route resolution - wait a tick to ensure routes are processed
    requestAnimationFrame(() => {
      console.log('[Router] Initial route resolution for:', window.location.pathname);
      console.log('[Router] Available routes:', Array.from(this.routes.keys()));
      this.resolveRoute(window.location.pathname);
    });
  }
  
  /**
   * Process ewc-route elements in the slot
   */
  private processRouteElements(): void {
    // Get all route elements in the light DOM
    const routeElements = Array.from(this.querySelectorAll('ewc-route'));
    console.log('[Router] Processing route elements, found:', routeElements.length);
    
    routeElements.forEach((route, index) => {
      const ewcRoute = route as EwcRouterRoute;
      if (ewcRoute.isProcessed()) {
        console.log(`[Router] Route ${index} already processed`);
        return;
      }
      
      const path = ewcRoute.path;
      const isDefault = ewcRoute.default;
      console.log(`[Router] Route ${index}: path="${path}", default=${isDefault}`);
      
      // Register the route
      if (path) {
        console.log(`[Router] Registering route: ${path}`);
        this.registerRoute(path, ewcRoute.getComponent());
      } else if (isDefault) {
        // Use '*' for default/404 route
        console.log('[Router] Registering default route');
        this.registerRoute('*', ewcRoute.getComponent());
      }
      
      // Mark as processed
      ewcRoute.markAsProcessed();
    });
    
    console.log('[Router] Routes after processing:', Array.from(this.routes.keys()));
  }
  
  /**
   * Register a route with a component renderer
   * @param path - The route path
   * @param component - Component function or object
   * @returns EwcRouterOutlet instance for chaining
   */
  registerRoute(path: string, component: EwcRoute): EwcRouterOutlet {
    this.routes.set(path, component);
    return this;
  }
  
  /**
   * Navigate programmatically to a path
   * @param path - Path to navigate to
   * @param replaceState - Whether to replace or push state
   */
  navigate(path: string, replaceState = false): void {
    if (path === this.currentPath) return;
    
    // Update history
    if (replaceState) {
      window.history.replaceState({ path }, '', path);
    } else {
      window.history.pushState({ path }, '', path);
    }
    
    this.resolveRoute(path);
  }
  
  /**
   * Handle browser back/forward navigation
   * @param event - PopState event
   */
  private handlePopState(event: PopStateEvent): void {
    const path = window.location.pathname;
    this.resolveRoute(path);
  }
  
  /**
   * Resolve and render the matching route
   * @param path - Path to resolve
   */
  private resolveRoute(path: string): void {
    console.log('[Router] Resolving route:', path);
    console.log('[Router] Available routes:', Array.from(this.routes.keys()));
    this.currentPath = path;
    
    // Find exact route match
    let component = this.routes.get(path);
    
    // Reset params
    this.currentParams = {};
    
    // If no direct match, try to find a parameterized route
    if (!component) {
      console.log('[Router] No exact match, trying parameterized routes...');
      // Look for routes with parameters
      for (const [routePath, routeComponent] of this.routes.entries()) {
        if (routePath.includes(':')) {
          const routeRegex = new RegExp('^' + routePath.replace(/:[^\s/]+/g, '([\\w-]+)') + '$');
          const match = path.match(routeRegex);
          
          if (match) {
            console.log('[Router] Found parameterized match:', routePath);
            component = routeComponent;
            // Extract parameters
            const paramNames = routePath.match(/:[^\s/]+/g) || [];
            const params: EwcRouteParams = {};
            
            paramNames.forEach((param, index) => {
              const paramName = param.substring(1); // Remove the colon
              params[paramName] = match[index + 1];
            });
            
            // Store params for the component to access
            this.currentParams = params;
            console.log('[Router] Extracted params:', params);
            break;
          }
        }
      }
    }
    
    // Default route (404) if no match found
    if (!component && this.routes.has('*')) {
      console.log('[Router] Using default route');
      component = this.routes.get('*');
    }
    
    if (!component) {
      console.log('[Router] No matching route found!');
    }
    
    // Request update to render the component
    this.requestUpdate();
    
    // Dispatch route change event
    this.dispatchEvent(new CustomEvent('route-changed', {
      detail: { path, params: this.currentParams },
      bubbles: true,
      composed: true
    }));
  }
  
  /**
   * Render the current route content
   * @returns Promise resolving to component template
   */
  private async renderRouteContent(): Promise<TemplateResult | HTMLElement | string | LazyRouteComponent | DocumentFragment> {
    const currentComponent = this.routes.get(this.currentPath);
    
    // If no direct match, try to find a parameterized route again
    let component = currentComponent;
    if (!component) {
      // Look for routes with parameters (doing it again here for rendering)
      for (const [routePath, routeComponent] of this.routes.entries()) {
        if (routePath.includes(':')) {
          const routeRegex = new RegExp('^' + routePath.replace(/:[^\s/]+/g, '([\\w-]+)') + '$');
          if (this.currentPath.match(routeRegex)) {
            component = routeComponent;
            break;
          }
        }
      }
      
      // 404 fallback
      if (!component && this.routes.has('*')) {
        component = this.routes.get('*');
      }
    }
    
    if (!component) {
      return html`<div>Page not found</div>`;
    }
    
    // Handle different component types
    try {
      this.loading = true;
      
      // Check if lazy loaded
      if (typeof component === 'object' && 'lazy' in component && component.lazy) {
        const loadedComponent = await component.component();
        
        if (typeof loadedComponent === 'function') {
          return loadedComponent(this.currentParams);
        } else {
          return loadedComponent;
        }
      } 
      // Check if function
      else if (typeof component === 'function') {
        // Handle async component functions
        const result = component(this.currentParams);
        if (result instanceof Promise) {
          return await result;
        }
        return result;
      } 
      // Otherwise assume it's a template or HTML
      else {
        return component;
      }
    } catch (error) {
      console.error('Failed to render route:', error);
      return html`<div>Error loading page</div>`;
    } finally {
      this.loading = false;
    }
  }
  
  /**
   * Get current route parameters
   * @returns Route parameters object
   */
  getParams(): EwcRouteParams {
    return { ...this.currentParams };
  }
  
  /**
   * LitElement render method
   */
  render(): TemplateResult {
    return html`
      <div class="router-content fade-in">
        ${this.loading ? 
          html`<div class="loading">Loading...</div>` : 
          html`<div id="route-content"></div>`
        }
      </div>
      <slot @slotchange=${() => {
        console.log('[Router] Slot changed, processing routes...');
        this.processRouteElements();
      }}></slot>
    `;
  }
  
  /**
   * Lifecycle method called after the component updates
   */
  updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    
    // Update the content after render if not loading
    if (changedProperties.has('currentPath')) {
      this.updateRouteContent();
    }
  }
  
  /**
   * Update the route content in the DOM
   */
  private async updateRouteContent(): Promise<void> {
    console.log('[Router] updateRouteContent called');
    const contentContainer = this.shadowRoot?.querySelector('#route-content') as HTMLElement;
    if (!contentContainer) {
      console.log('[Router] No content container found');
      return;
    }
  
    const content = await this.renderRouteContent();
    console.log('[Router] Content type:', content?.constructor?.name, content);
      
    // Clear previous content
    contentContainer.innerHTML = '';
    
    // Handle different content types
    if (content instanceof HTMLTemplateElement) {
      console.log('[Router] Processing HTMLTemplateElement');
      // Clone and append template content
      const clonedContent = content.content.cloneNode(true);
      contentContainer.appendChild(clonedContent);
      
      // Force web components to upgrade immediately
      const customElements = contentContainer.querySelectorAll(':not(:defined)');
      console.log('[Router] Found', customElements.length, 'undefined custom elements');
      customElements.forEach(el => {
        if (el.tagName.includes('-')) {
          console.log('[Router] Upgrading element:', el.tagName);
          window.customElements.upgrade(el);
        }
      });
      
      // Also upgrade any defined custom elements that might not be initialized
      const allCustomElements = contentContainer.querySelectorAll('*');
      allCustomElements.forEach(el => {
        if (el.tagName.includes('-') && window.customElements.get(el.tagName.toLowerCase())) {
          window.customElements.upgrade(el);
        }
      });
      
      // Dispatch connected event after a microtask to ensure components are ready
      Promise.resolve().then(() => {
        const event = new CustomEvent('route-content-loaded', {
          detail: { path: this.currentPath },
          bubbles: true,
          composed: true
        });
        this.dispatchEvent(event);
      });
    } else if (content instanceof DocumentFragment) {
      contentContainer.appendChild(content);
      
      // Force web components to upgrade
      const customElements = contentContainer.querySelectorAll(':not(:defined)');
      customElements.forEach(el => {
        if (el.tagName.includes('-')) {
          window.customElements.upgrade(el);
        }
      });
    } else if (content && typeof content === 'object' && 'strings' in content) {
      // It's a lit-html template result
      render(content as TemplateResult, contentContainer);
    } else if (content instanceof HTMLElement) {
      contentContainer.appendChild(content);
    } else if (typeof content === 'string') {
      contentContainer.innerHTML = content;
    } else {
      contentContainer.textContent = 'Invalid content';
    }
  }

  /**
   * Lifecycle method called when component is removed from DOM
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.handlePopState);
  }
}

/**
 * EwcRouterLink component for navigation
 * @element ewc-router-link
 */
@customElement('ewc-router-link')
export class EwcRouterLink extends LitElement {
  @property({ type: String }) href = '/';
  @property({ type: Boolean, reflect: true }) active = false;
  
  static styles = css`
    :host {
      display: inline-block;
    }
    
    a {
      color: #0077cc;
      text-decoration: none;
      cursor: pointer;
    }
    
    a:hover {
      text-decoration: underline;
    }
    
    :host([active]) a {
      font-weight: bold;
      color: #004499;
    }
  `;
  
  constructor() {
    super();
    
    // Update active state when route changes
    this.updateActiveState = this.updateActiveState.bind(this);
  }
  
  connectedCallback(): void {
    super.connectedCallback();
    this.updateActiveState();
    
    // Listen for route changes
    window.addEventListener('popstate', this.updateActiveState);
    document.addEventListener('route-changed', this.updateActiveState);
  }
  
  /**
   * Handle link click
   * @param e - Click event
   */
  private handleClick(e: Event): void {
    e.preventDefault();
    
    // Get router service or find ewc-router-outlet
    const routerService = (window as any).routerService as EwcRouterService;
    if (routerService) {
      routerService.navigate(this.href);
    } else {
      // Find router outlet
      const router = document.querySelector('ewc-router-outlet') as EwcRouterOutlet;
      if (router && typeof router.navigate === 'function') {
        router.navigate(this.href);
      } else {
        // Fallback to regular navigation
        window.location.href = this.href;
      }
    }
  }
  
  /**
   * Update active state based on current URL
   */
  private updateActiveState(): void {
    const currentPath = window.location.pathname;
    this.active = this.href === currentPath;
  }
  
  render(): TemplateResult {
    return html`
      <a href="${this.href}" 
         ewc-router-link
         @click="${this.handleClick}">
        <slot></slot>
      </a>
    `;
  }
  
  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.updateActiveState);
    document.removeEventListener('route-changed', this.updateActiveState);
  }
}



